"use client";

import type { RefObject } from "react";
import { useEffect, useRef, useState } from "react";

import { useIntersectedOnce } from "@/hooks/use-intersection-once";

export interface TermsMotionDebugProps {
  phase?: number;
  replayKey?: number;
  forceReducedMotion?: boolean;
}

export type TermsMotionState = "idle" | "playing" | "complete";

export const TERMS_MOTION_START_DELAY_MS = 300;

function clampPhase(phase: number): number {
  return Math.min(1, Math.max(0, phase));
}

function stateFromPhase(phase: number): TermsMotionState {
  if (phase <= 0) return "idle";
  if (phase >= 1) return "complete";
  return "playing";
}

function usePrefersReducedMotion(forceReducedMotion?: boolean): boolean {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (forceReducedMotion) return;

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [forceReducedMotion]);

  return forceReducedMotion === true || prefersReducedMotion;
}

export function useTermsCardMotion(
  ref: RefObject<HTMLElement | null>,
  durationMs: number,
  debug?: TermsMotionDebugProps
): {
  state: TermsMotionState;
  phase: number | undefined;
  reducedMotion: boolean;
} {
  const isInView = useIntersectedOnce(ref, { threshold: 0.15 });
  const reducedMotion = usePrefersReducedMotion(debug?.forceReducedMotion);
  const isDebugPhase = debug?.phase !== undefined;
  const normalizedPhase = isDebugPhase ? clampPhase(debug!.phase!) : undefined;
  const replayKey = debug?.replayKey;

  const [state, setState] = useState<TermsMotionState>(() => {
    if (isDebugPhase) return stateFromPhase(normalizedPhase!);
    return "complete";
  });

  const hasPlayedRef = useRef(false);
  const startDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameRef = useRef<number | null>(null);

  const clearStartDelay = () => {
    if (startDelayRef.current !== null) {
      clearTimeout(startDelayRef.current);
      startDelayRef.current = null;
    }
  };

  const clearPlaybackTimeout = () => {
    if (timeoutRef.current !== null) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  };

  const clearPlaybackFrame = () => {
    if (frameRef.current !== null) {
      cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }
  };

  const clearPlayback = () => {
    clearStartDelay();
    clearPlaybackFrame();
    clearPlaybackTimeout();
  };

  useEffect(() => {
    clearPlayback();
    hasPlayedRef.current = false;

    if (reducedMotion) {
      setState("complete");
      return;
    }

    if (isDebugPhase) {
      setState(stateFromPhase(normalizedPhase!));
      return;
    }

    setState("idle");
  }, [replayKey, reducedMotion, isDebugPhase, normalizedPhase]);

  useEffect(() => {
    if (!isDebugPhase || reducedMotion) return;
    setState(stateFromPhase(normalizedPhase!));
  }, [isDebugPhase, normalizedPhase, reducedMotion]);

  useEffect(() => {
    if (reducedMotion || isDebugPhase) return;
    if (!isInView || hasPlayedRef.current) return;

    startDelayRef.current = setTimeout(() => {
      startDelayRef.current = null;

      // Two frames ensure the idle state is painted before playback begins.
      // A single rAF can still run before the first paint after this effect.
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = requestAnimationFrame(() => {
          frameRef.current = null;
          hasPlayedRef.current = true;
          setState("playing");
          timeoutRef.current = setTimeout(() => {
            setState("complete");
            timeoutRef.current = null;
          }, durationMs);
        });
      });
    }, TERMS_MOTION_START_DELAY_MS);

    return clearPlayback;
  }, [isInView, reducedMotion, isDebugPhase, durationMs, replayKey]);

  useEffect(() => {
    return clearPlayback;
  }, []);

  return {
    state,
    phase: normalizedPhase,
    reducedMotion,
  };
}
